import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { FakturniaHorizontalNavigationComponent } from '@fakturnia/components/navigation/horizontal/horizontal.component';
import { FakturniaNavigationItem } from '@fakturnia/components/navigation/navigation.types';
import { NavigationService } from 'shared/services/navigation.service';
import { Subject, Subscription, filter, takeUntil } from 'rxjs';
import { FakturniaFullscreenComponent } from '@fakturnia/components/fullscreen/fullscreen.component';
import { UserComponent } from '@fakturnia/components/user/user.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AuthService } from 'shared/services/auth.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MediaWatcherService } from 'shared/services/media-watcher.service';
import { SettingsService } from 'shared/services/settings.service';
import { FakturniaNavigationService, FakturniaVerticalNavigationComponent } from '@fakturnia/components/navigation';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationBellComponent } from 'shared/components/notification-bell/notification-bell.component';

@Component({
  selector: 'modern-layout',
  standalone: true,
  imports: [CommonModule, MatButtonModule, RouterOutlet, MatTooltipModule, FakturniaVerticalNavigationComponent, FakturniaHorizontalNavigationComponent, UserComponent, MatProgressBarModule, FakturniaFullscreenComponent, RouterModule, MatIconModule, NotificationBellComponent],
  templateUrl: './modern-layout.component.html',
  styleUrl: './modern-layout.component.scss'
})
export class ModernLayoutComponent implements OnInit, OnDestroy {

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  public isScreenSmall: boolean;
  public navigation: FakturniaNavigationItem[] = []
  public horizontalNavigation: FakturniaNavigationItem[] = []
  public isSynchronizing = false
  public isLoggedIn = false
  public showManagement = true
  public showManager = true
  public showPlanner = true
  public showLedger = true
  public showGarage = true
  public showYardOps = true
  public isManagerActive = false
  public isPlannerActive = false
  public isLedgerActive = false
  public isGarageActive = false
  public isYardOpsActive = false
  public isAdminActive = false
  public plannerUpdatesCount = 0

  /** Routes under /account/management/ that belong to the Ledger section */
  private readonly _ledgerExtensions = ['/account/management/billing', '/account/management/financial-analysis', '/account/management/hr-daily', '/account/management/payroll'];

  /** Routes under /account/management/ that belong to the Manager section */
  private readonly _managerExtensions = ['/account/management/bid-center', '/account/management/project-management'];

  constructor(
    private _cdr: ChangeDetectorRef,
    private _navigationService: NavigationService,
    private _authService: AuthService,
    private _mediaWatcherService: MediaWatcherService,
    private _fakturniaNavigationService: FakturniaNavigationService,
    private _settingsService: SettingsService,
    private _router: Router
  ) {




  }

  /**
    * Toggle navigation
    *
    * @param name
    */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation = this._fakturniaNavigationService.getComponent<FakturniaVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  ngOnInit(): void {

    this._navigationService.currentNavigation
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (navigation) => {
          this.navigation = navigation
          this.horizontalNavigation = navigation.filter(item => item.id !== 'manager' && item.id !== 'management' && item.id !== 'ledger')
        }
      })

    this._navigationService.plannerUpdatesCount$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(count => {
        this.plannerUpdatesCount = count;
        this._cdr.markForCheck();
      });

    this._authService.isLoggedIn$.pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: (data) => {
        this.isLoggedIn = data
        this._cdr.markForCheck()
      }
    })

    // Subscribe to media changes
    this._mediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });

    this._settingsService.getUserSettings()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(settings => {
        const user = settings?.user;
        const isOwner = !user || user.role === 'user';
        this.showManagement = isOwner || (user.permissions && user.permissions.includes('VIEW_MANAGEMENT'));
        this.showManager = isOwner || (user.permissions && (
          user.permissions.includes('VIEW_PROJECT_MANAGEMENT') ||
          user.permissions.includes('VIEW_BILLING') ||
          user.permissions.includes('VIEW_CHECKLIST') ||
          user.permissions.includes('VIEW_FIELD_MEASURE')
        ));
        this.showPlanner = isOwner || (user.permissions && user.permissions.includes('VIEW_CHECKLIST'));
        this.showLedger = isOwner || (user.permissions && (
          user.permissions.includes('FINANCIAL_ANALYSIS') ||
          user.permissions.includes('VIEW_BILLING') ||
          user.permissions.includes('VIEW_TIMESHEET')
        ));
        this.showGarage = isOwner || (user.permissions && user.permissions.includes('VIEW_GARAGE'));
        this.showYardOps = isOwner || (user.permissions && (
          user.permissions.includes('VIEW_YARD_OPS') ||
          user.permissions.includes('VIEW_FUEL_LOG') ||
          user.permissions.includes('VIEW_DUMP_TICKET') ||
          user.permissions.includes('VIEW_INVENTORY')
        ));
        this._cdr.markForCheck();
      });

    // Track active nav button based on current URL
    this._updateActiveNav(this._router.url);
    this._router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this._unsubscribeAll)
    ).subscribe((e: NavigationEnd) => {
      this._updateActiveNav(e.urlAfterRedirects);
      this._cdr.markForCheck();
    });

    
  }

  ngOnDestroy(): void {

    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  private _updateActiveNav(url: string): void {
    const path = url.split('?')[0];
    const isLedgerExtension = this._ledgerExtensions.some(ext => path.startsWith(ext));
    const isManagerExtension = this._managerExtensions.some(ext => path.startsWith(ext));
    const isPlanner = path.startsWith('/account/management/planner');

    this.isPlannerActive = isPlanner;
    this.isManagerActive = (path === '/account/manager' || isManagerExtension) && !isPlanner;
    this.isLedgerActive = (path === '/account/ledger' || isLedgerExtension) && !isPlanner;
    this.isGarageActive = path.startsWith('/account/garage') && !isPlanner;
    this.isYardOpsActive = path.startsWith('/account/yard-ops') && !isPlanner;
    this.isAdminActive = path.startsWith('/account/management') && !isLedgerExtension && !isManagerExtension && !isPlanner;
  }
}
